import { useFlag } from '@reflag/react-sdk';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationChangeRequestAgentSideSheet,
    NavigationScreenSpaceState,
} from '@gitbook/app-navigation';
import { useHiveRevision } from '@gitbook/hive-react';
import { SidePanelSheet } from '@gitbook/react-uikit';

import {
    AgentBlankslateRequestAccess,
    AgentChangeRequestSidePanel,
    useCurrentHivePage,
} from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';

type ChangeRequestAgentSidePanelProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    screen: NavigationScreenSpaceState;
};

/**
 * Sidepanel for GitBook Agent for change request.
 */
export function ChangeRequestAgentSidePanel(
    props: ChangeRequestAgentSidePanelProps
): React.ReactElement | null {
    const { isEnabled } = useFlag('DOCS_AGENTS');
    const { close } = useSideSheetNavigation<NavigationChangeRequestAgentSideSheet>('agent');

    if (!isEnabled) {
        return (
            <SidePanelSheet
                title="Docs Agent"
                titleBadgeKind="alpha"
                titleBadgeLabel="Alpha"
                onClose={close}
                expandable
            >
                <AgentBlankslateRequestAccess />
            </SidePanelSheet>
        );
    }

    return <ChangeRequestAgentSidePanelEnabled {...props} />;
}

/**
 * Sidepanel when the feature is enabled,
 */
function ChangeRequestAgentSidePanelEnabled(
    props: ChangeRequestAgentSidePanelProps
): React.ReactElement | null {
    const { space, changeRequest, analyticsContext, screen } = props;
    const { close } = useSideSheetNavigation<NavigationChangeRequestAgentSideSheet>('agent');

    useSetAnalyticsScreen({ ...analyticsContext, screen: 'space_change_request_agent' }, []);

    // Get revision and page
    const { revision } = useHiveRevision();
    const [page] = useCurrentHivePage(revision, screen.page);

    return (
        <AgentChangeRequestSidePanel
            space={space}
            changeRequest={changeRequest}
            currentContext={{
                location: page
                    ? {
                          spaceId: space.id,
                          pageId: page.id,
                      }
                    : undefined,
            }}
            onClose={close}
        />
    );
}
